import { useMemo, type ReactNode } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";

import { useBrandingContext } from "@core";

import type { PropertiesTableLabels, TableRowData } from "./types";

type PropertiesTableProps = Readonly<{
  rows: TableRowData[];
  labels: PropertiesTableLabels;
  energyYearLabel: string;
  initialColumnVisibility?: Record<string, boolean>;
  enableSorting?: boolean;
  enableFilters?: boolean;
  enableGlobalFilter?: boolean;
  renderAddressCell: (row: TableRowData) => ReactNode;
}>;

export function PropertiesTable({
  rows,
  labels,
  energyYearLabel,
  initialColumnVisibility,
  enableSorting = true,
  enableFilters = true,
  enableGlobalFilter = true,
  renderAddressCell,
}: PropertiesTableProps) {
  const theme = useTheme();
  const { branding } = useBrandingContext();

  const columns = useMemo(
    () => [
      {
        header: labels.address,
        accessorKey: "address",
        enableHiding: false,
        enableSorting,
        Cell: ({ row }: { row: { original: TableRowData } }) => renderAddressCell(row.original),
      },
      ...(labels.externalID
        ? [
            {
              header: labels.externalID,
              // flex-substations returns the external id in `name`.
              accessorKey: "name",
              enableSorting: true,
              Cell: ({ cell }: { cell: { getValue: () => unknown } }) => {
                const value = cell.getValue();
                return (
                  <Typography variant="bodyMedium">
                    {typeof value === "string" && value.trim() ? value : "N/A"}
                  </Typography>
                );
              },
            },
          ]
        : []),
      {
        header: labels.flexStationId,
        accessorKey: "flexStationId",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingFlex) return <Skeleton animation="wave" width="70%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: labels.secondaryId,
        accessorKey: "secondaryId",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingFlex) return <Skeleton animation="wave" width="60%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: labels.numberOfSensors,
        accessorKey: "numberOfSensors",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingFlex) return <Skeleton animation="wave" width="40%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: `${labels.targetTemp} (°C)`,
        accessorKey: "targetTemp",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingFlex || row.original.isLoadingTemps)
            return <Skeleton animation="wave" width="50%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: `${labels.indoorTemp} - ${labels.currentAverage}`,
        Header: () => (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="labelLarge">{labels.indoorTemp}</Typography>
            <Typography
              variant="labelMedium"
              sx={{ color: branding.colors.fontSecondary }}
            >{`${labels.currentAverage} (°C)`}</Typography>
          </Box>
        ),
        accessorKey: "currentAvgTemp",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingTemps) return <Skeleton animation="wave" width="50%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: `${labels.indoorTemp} - ${labels.averageLast24h}`,
        Header: () => (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="labelLarge">{labels.indoorTemp}</Typography>
            <Typography
              variant="labelMedium"
              sx={{ color: branding.colors.fontSecondary }}
            >{`${labels.averageLast24h} (°C)`}</Typography>
          </Box>
        ),
        accessorKey: "last24hAvgTemp",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingTemps) return <Skeleton animation="wave" width="50%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
      {
        header: labels.energyUse,
        Header: () => (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="labelLarge">{labels.energyUse}</Typography>
            <Typography variant="labelMedium" sx={{ color: branding.colors.fontSecondary }}>
              {energyYearLabel}
            </Typography>
          </Box>
        ),
        accessorKey: "energyUse",
        enableSorting,
        Cell: ({ cell, row }: { cell: any; row: { original: TableRowData } }) => {
          if (row.original.isLoadingEnergy) return <Skeleton animation="wave" width="50%" />;
          return <Typography variant="bodyMedium">{cell.getValue() as string}</Typography>;
        },
      },
    ],
    [labels, energyYearLabel, branding, renderAddressCell, enableSorting]
  );

  // Table-level sorting must stay on for the external id column even when every
  // other column is unsorted. TanStack only sorts a column when both are enabled.
  const tableSortingEnabled = enableSorting || !!labels.externalID;

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableSorting: tableSortingEnabled,
    enableFilters,
    enableGlobalFilter,
    initialState: initialColumnVisibility
      ? { columnVisibility: initialColumnVisibility }
      : undefined,
    muiTablePaperProps: {
      sx: {
        backgroundColor: branding.colors.card,
        boxShadow: "none",
        borderRadius: 2,
        p: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: branding.colors.card,
        color: theme.palette.text.primary,
        borderBottomColor: branding.colors.divider2,
        ...theme.typography.labelLarge,
        "& .MuiTableSortLabel-root, & .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Wrapper, & .Mui-TableHeadCell-Content-Labels":
          {
            ...theme.typography.labelLarge,
          },
        "& .MuiTableSortLabel-root": {
          color: theme.palette.text.primary,
          "&.Mui-active": {
            color: theme.palette.primary.main,
          },
        },
        "& .MuiTableSortLabel-icon": {
          color: `${theme.palette.primary.main} !important`,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: theme.palette.text.primary,
        backgroundColor: branding.colors.card,
        borderBottomColor: branding.colors.divider2,
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: branding.colors.card,
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: branding.colors.card,
        ...theme.typography.labelMedium,
      },
    },
    muiPaginationProps: {
      sx: {
        ...theme.typography.labelMedium,
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input, & .MuiTablePagination-actions, & .MuiSelect-select, & .MuiInputBase-input, & .MuiMenuItem-root, & .MuiPaginationItem-root, & .MuiTypography-root":
          {
            ...theme.typography.labelMedium,
          },
      },
    },
  });

  return <MaterialReactTable table={table} />;
}
